import { DatePipe, NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    Input,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { Observable } from "rxjs";
import { ListColumnDirective } from "../list/list-column.directive";
import { ListComponent } from "../list/list.component";
import { TextExpanderComponent } from "../text-expander/text-expander.component";
import { IVerksamhetshandelseRequest, IVerksamhetshandelseResponse } from "./contracts/verksamhetshandelse";
import { VerksamhetshandelseService } from "./verksamhetshandelse.service";
import { VisningsloggService } from "./visningslogg.service";

@Component({
    selector: "rask-verksamhetshandelse-logg",
    styleUrls: ["./verksamhetshandelseLogg.component.scss"],
    templateUrl: "./verksamhetshandelseLogg.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ListComponent,
        ListColumnDirective,
        NgClass,
        TextExpanderComponent,
        DatePipe
    ]
})

export class VerksamhetshandelseLoggComponent {
    @Input() rows = 2;
    @Output() sumOlastaHandelser: EventEmitter<string> = new EventEmitter<string>();
    updatable = false;
    verksamhetsHandelserResponse: IVerksamhetshandelseResponse[];

    private readonly verksamhetshandelseService = inject(VerksamhetshandelseService);
    private readonly visningsloggService = inject(VisningsloggService);

    private olastaHandelserShowPlusSignValue = 9;
    private updateDone = false;
    private verksamhetsHandelseRequest: IVerksamhetshandelseRequest;

    @Input() set criteria(value: IVerksamhetshandelseRequest) {
        if (value === null) {
            return;
        }

        this.verksamhetsHandelseRequest = value;
        this.getHandelseloggar().subscribe((response) => {
            this.updateDone = false;
            this.verksamhetsHandelserResponse = response;

            this.showNumberOfOlastaHandelser(this.verksamhetsHandelserResponse);
        });
    }

    @Input() set handelserReadDone(value: boolean) {
        if (value === undefined) {
            return;
        }

        this.updatable = value;
        if (!this.updatable) {
            this.verksamhetsHandelserResponse?.forEach(x => x.ny = false);
        }

        if (!this.updateDone && this.updatable) {
            this.updateNyaHandelseloggar().subscribe((response) => {
                this.sumOlastaHandelser.emit("");
                this.updateDone = true;
            });
        }
    }

    get hasUnreadData(): boolean {
        return !!this.verksamhetsHandelserResponse?.find(x => x.ny === true);
    }

    @Input() set olastaHandelserShowPlusSignAt(value: number) {
        this.olastaHandelserShowPlusSignValue = value;
    }

    private formatText(antalOlastaHandelser: number): string {
        if (antalOlastaHandelser <= 0) {
            return "";
        }

        const antalOlastaHandelserText = antalOlastaHandelser.toString();
        const olastaHandelserShowPlusSignValueText = this.olastaHandelserShowPlusSignValue.toString().concat("+");
        const antalOlastaHandelserSum = antalOlastaHandelser <= 0 ? 0 : antalOlastaHandelser;

        return antalOlastaHandelserSum >= this.olastaHandelserShowPlusSignValue ? olastaHandelserShowPlusSignValueText : antalOlastaHandelserText;
    }

    private getHandelseloggar(): Observable<IVerksamhetshandelseResponse[]> {
        return this.verksamhetshandelseService.getVerksamhetshandelse(this.verksamhetsHandelseRequest);
    }

    private showNumberOfOlastaHandelser(response: IVerksamhetshandelseResponse[]): void {
        const olastaHandelser = response.filter((data) => data.ny === true);

        this.sumOlastaHandelser.emit(this.formatText(olastaHandelser.length));
    }

    private updateNyaHandelseloggar(): Observable<IVerksamhetshandelseResponse[]> {
        if (!this.updatable) {
            return null;
        }

        return this.visningsloggService.createOrUpdateVisningslogg(this.verksamhetsHandelseRequest);
    }
}
